/**
 * useAnchorAttestation — read the caller's active Phase H attestation row.
 *
 * Phase H (sacred/email-change-attestation.md):
 *   When an Orchist proposes an anchor change via core.propose_anchor_change,
 *   an EmailChangeAttestation row is created. RLS limits SELECT to the
 *   caller's own rows (`Contact.userId = auth.uid()`).
 *
 * Returns the SINGLE active attestation (partial unique index enforces
 * one-active-per-Contact). If none, returns null.
 *
 * Refresh on demand via `refresh()` — useful after propose/cancel/etc.
 */

import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';

export interface AttestationRow {
  id: string;
  contactId: string;
  oldEmail: string;
  newEmail: string;
  proposedAt: string;
  oldAttestedAt: string | null;
  newAttestedAt: string | null;
  completedAt: string | null;
  cancelledAt: string | null;
  expiresAt: string;
  status:
    | 'pending'
    | 'old_only_attested'
    | 'new_only_attested'
    | 'both_attested'
    | 'completed'
    | 'expired'
    | 'cancelled'
    | 'rejected_by_old'
    | 'rejected_by_new';
}

const ATTESTATION_COLUMNS =
  'id, contactId, oldEmail, newEmail, proposedAt, oldAttestedAt, newAttestedAt, completedAt, cancelledAt, expiresAt, status';

const ACTIVE_STATUSES = [
  'pending',
  'old_only_attested',
  'new_only_attested',
  'both_attested',
];

export function useAnchorAttestation() {
  const { contact } = useAuth();
  const [active, setActive] = useState<AttestationRow | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const refresh = useCallback(() => setRefreshKey((k) => k + 1), []);

  useEffect(() => {
    if (!contact) {
      setActive(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      const { data, error: err } = await supabase
        .schema('core')
        .from('EmailChangeAttestation')
        .select(ATTESTATION_COLUMNS)
        .eq('contactId', contact.id)
        .in('status', ACTIVE_STATUSES)
        .order('proposedAt', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (cancelled) return;
      if (err) {
        // Empty result is not an error; only true errors set state.
        // PostgREST returns 'PGRST116' for "no rows from .single()" — ignore.
        if (err.code !== 'PGRST116') {
          setError(new Error(err.message));
        }
        setActive(null);
        setLoading(false);
        return;
      }
      setActive((data as AttestationRow | null) ?? null);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [contact, refreshKey]);

  const propose = useCallback(
    async (newEmail: string) => {
      const { data, error: err } = await supabase
        .schema('core')
        .rpc('propose_anchor_change', { p_new_email: newEmail });
      if (err) throw new Error(err.message);
      refresh();
      return data as { attestationId: string; oldEmail: string; newEmail: string; expiresAt: string };
    },
    [refresh]
  );

  const cancel = useCallback(async () => {
    const { error: err } = await supabase
      .schema('core')
      .rpc('cancel_anchor_change');
    if (err) throw new Error(err.message);
    refresh();
  }, [refresh]);

  return {
    active,
    loading,
    error,
    refresh,
    propose,
    cancel,
  };
}
